import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Sun, Moon, LogOut, User, Building2, Shield, Mail, ChevronRight, Calendar, Wrench, Loader2, CheckCircle, AlertTriangle, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const isAdminOrSuperadmin = user?.role === "admin" || user?.role === "superadmin";
  const isSuperadmin = user?.role === "superadmin";

  const handleRepairLegacyInvoices = async () => {
    setRepairLoading(true);
    setRepairResult(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/repair-legacy-invoices`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      // Clone the response in case we need to read it twice
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        let errorText;
        try {
          errorText = await responseClone.text();
        } catch {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorText || "Unbekannter Fehler");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || "Fehler bei der Reparatur");
      }
      
      setRepairResult(data);
      
      if (data.repaired > 0) {
        toast.success(`${data.repaired} Rechnungen erfolgreich repariert!`);
      } else {
        toast.info("Alle Rechnungen sind bereits korrekt.");
      }
    } catch (error) {
      console.error("Repair failed:", error);
      const errorMessage = error.message || "Unbekannter Fehler bei der Reparatur";
      toast.error(errorMessage);
      setRepairResult({ error: errorMessage });
    } finally {
      setRepairLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-in max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Einstellungen</h1>
        <p className="text-muted-foreground">
          Verwalten Sie Ihre Kontoeinstellungen
        </p>
      </div>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profil
          </CardTitle>
          <CardDescription>Ihre Kontoinformationen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xl font-bold text-primary">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            <div>
              <p className="text-xl font-semibold">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Rolle</p>
              <p className="font-medium flex items-center gap-1">
                <Shield className="h-4 w-4" />
                {user?.role === "superadmin" ? "Super Admin" : user?.role === "admin" ? "Admin" : "Vertriebler"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Firmen</p>
              <p className="font-medium flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {user?.company_ids?.length || 0} zugewiesen
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings - Only for Admin/Superadmin */}
      {isAdminOrSuperadmin && (
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate("/einstellungen/email")}
          data-testid="email-settings-card"
        >
          <CardContent className="flex items-center justify-between py-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">E-Mail-Einstellungen</p>
                <p className="text-sm text-muted-foreground">
                  SMTP-Server für Kaufverträge und Auftragsbestätigungen konfigurieren
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </CardContent>
        </Card>
      )}

      {/* Appointment Templates - Only for Admin/Superadmin */}
      {isAdminOrSuperadmin && (
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate("/einstellungen/termin-vorlagen")}
          data-testid="appointment-templates-card"
        >
          <CardContent className="flex items-center justify-between py-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium">Termin-Textvorlagen</p>
                <p className="text-sm text-muted-foreground">
                  Standardtexte für Vor-Ort-Termine und Telefontermine bearbeiten
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </CardContent>
        </Card>
      )}

      {/* SMS & WhatsApp Settings - Only for Admin/Superadmin */}
      {isAdminOrSuperadmin && (
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate("/einstellungen/messaging")}
          data-testid="messaging-settings-card"
        >
          <CardContent className="flex items-center justify-between py-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium">SMS & WhatsApp</p>
                <p className="text-sm text-muted-foreground">
                  Twilio-Integration für automatische Terminbenachrichtigungen
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </CardContent>
        </Card>
      )}

      {/* Admin Tools - Only for Superadmin */}
      {isSuperadmin && (
        <Card data-testid="admin-tools-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Admin-Werkzeuge
            </CardTitle>
            <CardDescription>
              Systemwartung und Datenreparatur (nur für Superadmin)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Repair Legacy Invoices */}
            <div className="p-4 border rounded-lg space-y-3">
              <div>
                <p className="font-medium">Legacy-Rechnungen reparieren</p>
                <p className="text-sm text-muted-foreground">
                  Repariert Rechnungen aus importierten Angeboten (fehlende Positionen, Angebotsnummern, etc.)
                </p>
              </div>
              
              <Button 
                onClick={handleRepairLegacyInvoices}
                disabled={repairLoading}
                variant="outline"
                data-testid="repair-legacy-invoices-btn"
              >
                {repairLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Repariere...
                  </>
                ) : (
                  <>
                    <Wrench className="h-4 w-4 mr-2" />
                    Reparatur starten
                  </>
                )}
              </Button>
              
              {/* Repair Result */}
              {repairResult && !repairResult.error && (
                <div className="mt-3 p-3 bg-muted rounded-lg text-sm space-y-2">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    {repairResult.message}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                    <span>Legacy-Angebote: {repairResult.legacy_offers_found}</span>
                    <span>Rechnungen gefunden: {repairResult.invoices_found}</span>
                    <span className="text-green-600">Repariert: {repairResult.repaired}</span>
                    {repairResult.failed > 0 && (
                      <span className="text-red-600">Fehlgeschlagen: {repairResult.failed}</span>
                    )}
                  </div>
                  {repairResult.details && repairResult.details.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        Details anzeigen ({repairResult.details.length} Einträge)
                      </summary>
                      <ul className="mt-2 space-y-1 text-xs max-h-40 overflow-y-auto">
                        {repairResult.details.map((detail, idx) => (
                          <li key={idx} className="font-mono">{detail}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              
              {repairResult?.error && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg text-sm">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    {repairResult.error}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Darstellung</CardTitle>
          <CardDescription>Passen Sie das Erscheinungsbild an</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === "dark" ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
              <div>
                <Label>Dunkles Design</Label>
                <p className="text-sm text-muted-foreground">
                  Wechseln Sie zwischen hellem und dunklem Modus
                </p>
              </div>
            </div>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={toggleTheme}
              data-testid="settings-theme-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Logout */}
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">Sitzung beenden</CardTitle>
          <CardDescription>Melden Sie sich von Ihrem Konto ab</CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="destructive" 
            onClick={logout}
            data-testid="settings-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Abmelden
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
